import type { FormRule } from 'antd';
import { isValidPhoneNumber as isValidForCountry } from 'libphonenumber-js';

export const PHONE_NUMBER_FORMAT = '(###) ###-####';

// The lookahead rejects numbers where all 10 digits repeat (e.g. (111) 111-1111).
export const PHONE_NUMBER_PATTERN = /^(?!\((\d)\1\1\) \1\1\1-\1\1\1\1$)\(\d{3}\) \d{3}-\d{4}$/;

export const PHONE_NUMBER_VALIDATION_MESSAGE =
  'Please enter a valid Canadian or US phone number in the format (XXX) XXX-XXXX.';

// Mirrors the backend's phoneNumberValidation so the API never rejects a number we accepted.
export const isValidPhoneNumber = (value: string): boolean =>
  PHONE_NUMBER_PATTERN.test(value) && (isValidForCountry(value, 'CA') || isValidForCountry(value, 'US'));

export const phoneNumberRule: FormRule = {
  validator: (_, value?: string | null) =>
    !value || isValidPhoneNumber(value)
      ? Promise.resolve()
      : Promise.reject(new Error(PHONE_NUMBER_VALIDATION_MESSAGE)),
};

// Reformats any punctuation style (or a leading "1" country code) into (XXX) XXX-XXXX;
// values that don't resolve to exactly 10 digits are returned unchanged rather than guessed at.
export const normalizePhoneNumber = <T extends string | null | undefined>(value: T): T | string => {
  if (!value) return value;

  let digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  if (digits.length !== 10) return value;

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};
